import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import phaseBillingService from '../../../services/phaseBillingService';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { toast } from 'sonner';

/**
 * Composant de configuration des phases de facturation pour un package
 */
const PackageBillingPhasesConfig = ({ packageId, packageName, currencyCode = 'EUR', onSave }) => {
  const [phases, setPhases] = useState([
    { phaseName: 'Phase 1 - Initiale', rangeFrom: 0, rangeTo: 26, price: 100000 },
    { phaseName: 'Phase 2 - Intermédiaire', rangeFrom: 27, rangeTo: 52, price: 80000 },
    { phaseName: 'Phase 3 - Finale', rangeFrom: 53, rangeTo: 999, price: 60000 }
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (packageId) fetchExistingConfig();
  }, [packageId]);

  const fetchExistingConfig = async () => {
    try {
      setLoading(true);
      const config = await phaseBillingService.getBillingPhasesConfig(packageId);
      
      if (config.length > 0) {
        setPhases(config.map(p => ({
          id: p.id,
          phaseName: p.phase_name,
          rangeFrom: p.aligner_range_from,
          rangeTo: p.aligner_range_to,
          price: parseFloat(p.phase_price)
        })));
      }
    } catch (error) {
      console.error('Error fetching billing phases config:', error);
      toast.error('Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currencyCode || 'EUR',
      }).format(value ?? 0);
    } catch {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
      }).format(value ?? 0);
    }
  };

  const validatePhases = () => {
    const newErrors = {};
    let isValid = true;

    phases.forEach((phase, index) => {
      // Validation du nom
      if (!phase.phaseName || phase.phaseName.trim() === '') {
        newErrors[`${index}-name`] = 'Le nom de la phase est requis';
        isValid = false;
      }

      // Validation des plages
      if (phase.rangeFrom < 0) {
        newErrors[`${index}-rangeFrom`] = 'La plage doit être >= 0';
        isValid = false;
      }

      if (phase.rangeTo <= phase.rangeFrom) {
        newErrors[`${index}-rangeTo`] = 'La fin doit être > au début';
        isValid = false;
      }

      // Validation du prix
      if (phase.price <= 0) {
        newErrors[`${index}-price`] = 'Le prix doit être > 0';
        isValid = false;
      }

      // Vérification des chevauchements
      if (index > 0) {
        const prevPhase = phases[index - 1];
        if (phase.rangeFrom <= prevPhase.rangeTo) {
          newErrors[`${index}-rangeFrom`] = 'Chevauchement avec la phase précédente';
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handlePhaseChange = (index, field, value) => {
    const newPhases = [...phases];
    newPhases[index][field] = field === 'price' || field === 'rangeFrom' || field === 'rangeTo' 
      ? parseFloat(value) || 0 
      : value;
    setPhases(newPhases);
    
    // Clear errors for this field
    const newErrors = { ...errors };
    delete newErrors[`${index}-${field}`];
    setErrors(newErrors);
  };

  const addPhase = () => {
    const lastPhase = phases[phases.length - 1];
    const newPhase = {
      phaseName: `Phase ${phases.length + 1}`,
      rangeFrom: lastPhase ? lastPhase.rangeTo + 1 : 0,
      rangeTo: lastPhase ? lastPhase.rangeTo + 26 : 26,
      price: lastPhase ? lastPhase.price * 0.8 : 50000
    };
    setPhases([...phases, newPhase]);
  };

  const removePhase = (index) => {
    if (phases.length <= 1) {
      toast.error('Vous devez avoir au moins une phase');
      return;
    }
    const newPhases = phases.filter((_, i) => i !== index);
    setPhases(newPhases);
  };

  const handleSave = async () => {
    if (!validatePhases()) {
      toast.error('Veuillez corriger les erreurs avant de sauvegarder');
      return;
    }

    try {
      setSaving(true);
      await phaseBillingService.updatePackageBillingPhases(packageId, phases);
      toast.success('Configuration des phases mise à jour avec succès !');
      if (onSave) onSave();
    } catch (error) {
      console.error('Error saving billing phases:', error);
      toast.error('Erreur lors de la sauvegarde', { description: error.message });
    } finally {
      setSaving(false);
    }
  };

  const getTotalPrice = () => {
    return phases.reduce((sum, phase) => sum + (parseFloat(phase.price) || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Icon name="Loader" size={24} className="animate-spin text-primary mr-3" />
        <span className="text-muted-foreground">Chargement de la configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="Settings" size={20} className="text-primary" />
            Configuration des phases de facturation
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Package: {packageName} — Définissez les paliers de facturation par nombre d'aligneurs
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={addPhase}
          iconName="Plus"
          iconPosition="left"
        >
          Ajouter une phase
        </Button>
      </div>

      {/* Info card */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-foreground mb-1">
              Fonctionnement de la facturation par phases
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• La facturation se déclenche automatiquement au <strong>début de la production</strong> d'une nouvelle phase</li>
              <li>• Chaque phase est facturée <strong>une seule fois</strong> lorsque le nombre d'aligneurs entre dans sa plage</li>
              <li>• Si le traitement s'arrête avant une phase, celle-ci n'est <strong>pas facturée</strong></li>
              <li>• Les plages ne doivent <strong>pas se chevaucher</strong></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Phases List */}
      <div className="space-y-4">
        {phases.map((phase, index) => (
          <div
            key={index}
            className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-bold text-primary">{index + 1}</span>
                </div>
                <input
                  type="text"
                  value={phase.phaseName}
                  onChange={(e) => handlePhaseChange(index, 'phaseName', e.target.value)}
                  className={`px-3 py-1.5 border rounded-lg bg-background text-foreground font-medium focus:outline-none focus:ring-2 focus:ring-primary ${
                    errors[`${index}-name`] ? 'border-destructive' : 'border-border'
                  }`}
                  placeholder="Nom de la phase"
                />
                {errors[`${index}-name`] && (
                  <span className="text-xs text-destructive">{errors[`${index}-name`]}</span>
                )}
              </div>
              {phases.length > 1 && (
                <button
                  onClick={() => removePhase(index)}
                  className="p-2 rounded-lg hover:bg-destructive/10 text-destructive transition-colors"
                  title="Supprimer cette phase"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Plage de début */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  De (aligneurs)
                </label>
                <input
                  type="number"
                  min="0"
                  value={phase.rangeFrom}
                  onChange={(e) => handlePhaseChange(index, 'rangeFrom', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
                    errors[`${index}-rangeFrom`] ? 'border-destructive' : 'border-border'
                  }`}
                />
                {errors[`${index}-rangeFrom`] && (
                  <p className="text-xs text-destructive mt-1">{errors[`${index}-rangeFrom`]}</p>
                )}
              </div>

              {/* Plage de fin */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  À (aligneurs)
                </label>
                <input
                  type="number"
                  min="0"
                  value={phase.rangeTo}
                  onChange={(e) => handlePhaseChange(index, 'rangeTo', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
                    errors[`${index}-rangeTo`] ? 'border-destructive' : 'border-border'
                  }`}
                />
                {errors[`${index}-rangeTo`] && (
                  <p className="text-xs text-destructive mt-1">{errors[`${index}-rangeTo`]}</p>
                )}
              </div>

              {/* Prix */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Prix de la phase
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={phase.price}
                  onChange={(e) => handlePhaseChange(index, 'price', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
                    errors[`${index}-price`] ? 'border-destructive' : 'border-border'
                  }`}
                />
                {errors[`${index}-price`] && (
                  <p className="text-xs text-destructive mt-1">{errors[`${index}-price`]}</p>
                )}
              </div>
            </div>

            {/* Phase Summary */}
            <div className="mt-3 p-3 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Résumé:</strong> De {phase.rangeFrom} à {phase.rangeTo} aligneurs
                {' '} → <strong className="text-primary">{formatCurrency(phase.price)}</strong>
                {' '}({phase.rangeTo - phase.rangeFrom + 1} aligneurs dans cette plage)
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Total Summary */}
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Prix total si toutes les phases sont facturées</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {phases.length} phase{phases.length > 1 ? 's' : ''} configurée{phases.length > 1 ? 's' : ''}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-primary">{formatCurrency(getTotalPrice())}</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-4 border-t border-border">
        <Button
          variant="default"
          onClick={handleSave}
          loading={saving}
          disabled={saving}
          iconName="Save"
          iconPosition="left"
        >
          Enregistrer la configuration
        </Button>
        <Button
          variant="outline"
          onClick={fetchExistingConfig}
          disabled={saving}
          iconName="RotateCcw"
          iconPosition="left"
        >
          Réinitialiser
        </Button>
      </div>
    </div>
  );
};

export default PackageBillingPhasesConfig;